/* All units below derive from --legacy-base (= 15px), declared on
   .article in ContentLayoutLEGACY.module.css. The article CSS module
   intentionally avoids `--article-*` design tokens — those are owned
   by the non-LEGACY HtmlArticle module and must not be retargeted. */

.title {
    font-family: inherit;
    font-size: 1.625em;
    font-weight: 500;
    line-height: 1.35;
    color: #333;
    background: white;
    /* Negative horizontal margin cancels the .article column's 22.5px
       padding so the sticky title's background spans the full column
       width — otherwise sub-header hover backgrounds (which extend
       slightly beyond .content via negative margin) bleed past the
       title's edges. Padding restores the text's visual position. */
    margin: var(--legacy-base) calc(var(--legacy-base) * -1.5);
    padding: 0 calc(var(--legacy-base) * 1.5);

    position: sticky;
    top: 0;
    z-index: 1;
    /* Scroll-state query container so .title::after can react to the
       sticky `stuck: top` state below. Must live on the sticky element
       itself (the scroller, .article, is never "stuck"). */
    container-type: scroll-state;
}

/* Verbatim port of legacy #document-content-wrapper::before: a wide,
   side-inset box rounded to an ellipse (border-radius:50%) whose offsetless
   blur casts a soft, center-weighted shadow. The caster itself is invisible
   (transparent) — only its rounded shadow shows. It rides the title's pseudo
   (rather than the scroll container's, like legacy) because here the title
   is sticky *inside* the scroller, so the title's bottom — not the scroller's
   top — is the boundary the shadow must hug, and a title pseudo tracks that
   edge automatically.
     - clip-path hides everything at/above the box's bottom edge so only the
       downward shadow is seen (legacy hides the body above the scroller's
       overflow edge instead).
     - text is hidden as it scrolls past because the title's opaque white
       background covers everything above that same edge. */
.title::after {
    content: "";
    position: absolute;
    left: var(--legacy-base);
    right: var(--legacy-base);
    bottom: 0;
    height: calc(var(--legacy-base) * 2);
    border-radius: 50%;
    pointer-events: none;
    box-shadow: 0 0 var(--legacy-base) rgb(61 61 61 / 0.5);
    clip-path: inset(100% calc(var(--legacy-base) * -2) calc(var(--legacy-base) * -2) calc(var(--legacy-base) * -2));
    opacity: 0;
    transition: opacity 0.2s ease;
}

@container scroll-state(stuck: top) {
    .title::after {
        opacity: 1;
    }
}

.description {
    border-bottom: 1px solid rgb(226, 226, 226);
    margin: 0 0 calc(var(--legacy-base) * 2);
    padding: 0 0 calc(var(--legacy-base) * 2);
}

.description p {
    margin: 0 0 calc(var(--legacy-base) * 0.5);
}

.description p:last-child {
    margin-bottom: 0;
}

/* Headings + paragraphs are intentionally unlayered. theme.css carries
   an unlayered global rule `h2, h3, h4 { font-weight: 400 }`, which
   beats any layered rule regardless of specificity. Keeping these out
   of @layer article-base lets them win on specificity (`.content h2`
   = 0,1,1 vs `h2` = 0,0,1). */

.content h1,
.content h2,
.content h3,
.content h4,
.content h5,
.content h6 {
    font-family: inherit;
    font-weight: 500;
    color: #333;
    margin: var(--legacy-base) 0;
}

.content h1 {
    font-size: 1.625em;
    line-height: 1.35;
}

.content h2 {
    font-size: 1.375em;
    line-height: 1.4;
}

.content h3 {
    font-size: 1.125em;
    line-height: 1.45;
}

.content h4,
.content h5,
.content h6 {
    font-size: 1em;
    line-height: 1.55;
}

.content {
    /* margin: var(--legacy-base) 0; */
    p {
        margin-top: 0;
        margin-bottom: 0.875rem;
        text-wrap: pretty;
    }
}

/* Element-level resets live in @layer article-base so that component
   CSS modules (PanelLink, AutoLink, Xbox, Table, etc.) automatically
   win the cascade without specificity hacks. Mirrors the layer
   structure in HtmlArticle.module.css. */
@layer article-base {
    .content {
        word-break: break-word;
        /* Lets the IntersectionObserver scroll-spy reach the last
           section when scrolled to the bottom. Mirrors the non-LEGACY
           .articleInner 50vh bottom margin in ContentLayout.module.css. */
        margin-bottom: 50vh;
    }

    .content ul,
    .content ol {
        margin: var(--legacy-base) 0;
    }

    .content section[id] {
        margin-bottom: calc(var(--legacy-base) * 1.5);
    }
}

/* Inline link parity (LEGACY). Unlayered to beat the unlayered
   `.navLink` rule in components/contentLink.module.css. Hardcoded blue
   per TASK_FINALIZED (not factored with #1). */
.content :global(a)[href]:not([href="#"]) {
    color: rgb(43, 102, 199);
    text-decoration: none;
}

.content :global(a)[href]:not([href="#"]):hover {
    text-decoration: underline;
}
